import React, { createContext, useEffect, useState } from 'react';
import url from '../../components/url';

// Create a context
export const QuoteContext = createContext();
const BOOKING_SESSION_KEY = 'courierLinkBookingSession';

// Create a provider component
export function QuoteProvider({ children }) {
    const defaultFormData = {
        pickupAddress: '',
        deliveryAddress: '',
        packageType: '',
        weight: '',
        goodsValue: '',
        dangerousGoods: '',
        pickupAddressType: '',
        deliveryAddressType: '',
        length: '',
        width: '',
        height: '',
        specialHandling: '',
        pickupPostcode: '',
        deliveryPostcode: '',
        pickupCity: '',
        deliveryCity: '',
        pickupState: '',
        deliveryState: '',
        originCountry: 'AU',
        destinationCountry: 'AU',
        palletStackable: null
    };
    const [restoredSession] = useState(() => {
        try {
            const savedSession = sessionStorage.getItem(BOOKING_SESSION_KEY);
            return savedSession ? JSON.parse(savedSession) : {};
        } catch (error) {
            sessionStorage.removeItem(BOOKING_SESSION_KEY);
            return {};
        }
    });
    const [quoteData, setQuoteData] = useState(restoredSession.quoteData || null);
    const [quoteRequestBody, setQuoteRequestBody] = useState(restoredSession.quoteRequestBody || null);
    const [selectedQuote, setSelectedQuote] = useState(restoredSession.selectedQuote || null);
    const [SRDetails, setSRDetails] = useState(restoredSession.SRDetails || null);
    const [formData, setFormData] = useState({ ...defaultFormData, ...(restoredSession.formData || {}) });
    const [draftId, setDraftId] = useState(restoredSession.draftId || null);
    const [draftUpdatedAt, setDraftUpdatedAt] = useState(restoredSession.draftUpdatedAt || null);

    useEffect(() => {
        try {
            sessionStorage.setItem(BOOKING_SESSION_KEY, JSON.stringify({
                quoteData,
                quoteRequestBody,
                selectedQuote,
                SRDetails,
                formData,
                draftId,
                draftUpdatedAt,
            }));
        } catch (error) {
            console.error('Unable to preserve booking progress:', error);
        }
    }, [quoteData, quoteRequestBody, selectedQuote, SRDetails, formData, draftId, draftUpdatedAt]);


    // Function to save quotes
    const saveQuote = (data, requestBody, selectQuote) => {
        setQuoteData(data);
        setQuoteRequestBody(requestBody);
        setSelectedQuote(selectQuote);
    };

    // Function to save SR details
    const saveSRDetails = (srDetails) => {
        setSRDetails(srDetails); 
    };

    // Function to update formData
    const saveFormData = (newFormData) => {
        setFormData((prev) => ({ ...prev, ...newFormData })); // Merge new data with existing state
    };

    const saveDraft = async (currentStep = 'quote', overrides = {}) => {
        const token = localStorage.getItem('token');
        if (!token) {
            const error = new Error('Please sign in or create an account to save a draft.');
            error.code = 'AUTH_REQUIRED';
            throw error;
        }

        const destination = overrides.formData?.deliveryCity || formData.deliveryCity;
        const snapshot = {
            title: overrides.title || (destination ? `Shipment to ${destination}` : 'Untitled shipment'),
            currentStep,
            formData: overrides.formData || formData,
            quoteData: overrides.quoteData === undefined ? quoteData : overrides.quoteData,
            quoteRequestBody: overrides.quoteRequestBody === undefined ? quoteRequestBody : overrides.quoteRequestBody,
            selectedQuote: overrides.selectedQuote === undefined ? selectedQuote : overrides.selectedQuote,
            srDetails: overrides.srDetails === undefined ? SRDetails : overrides.srDetails,
        };

        const response = await fetch(`${url}/api/drafts${draftId ? `/${draftId}` : ''}`, {
            method: draftId ? 'PUT' : 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(snapshot),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.message || 'Unable to save this draft.');
        }

        const savedDraft = data.draft;
        setDraftId(savedDraft.id);
        setDraftUpdatedAt(savedDraft.updatedAt);
        return savedDraft;
    };

    const restoreDraft = (draft) => {
        setDraftId(draft.id);
        setDraftUpdatedAt(draft.updatedAt);
        setFormData({ ...defaultFormData, ...(draft.formData || {}) });
        setQuoteData(draft.quoteData || null);
        setQuoteRequestBody(draft.quoteRequestBody || null);
        setSelectedQuote(draft.selectedQuote || null);
        setSRDetails(draft.srDetails || null);
    };

    const resetBookingFlow = () => {
        setQuoteData(null);
        setQuoteRequestBody(null);
        setSelectedQuote(null);
        setSRDetails(null);
        setFormData(defaultFormData);
        setDraftId(null);
        setDraftUpdatedAt(null);
    };

    const applyBookingTemplate = (booking) => {
        setQuoteData(null);
        setQuoteRequestBody(null);
        setSelectedQuote(null);
        setDraftId(null);
        setDraftUpdatedAt(null);
        setFormData({
            ...defaultFormData,
            pickupAddress: booking.From_addr1 || '',
            deliveryAddress: booking.To_addr1 || '',
            packageType: booking.P_Type || '',
            weight: booking.P_Weight || '',
            goodsValue: booking.GoodsValue || '',
            pickupAddressType: booking.From_addr_type || '',
            deliveryAddressType: booking.To_addr_type || '',
            length: booking.D_Length || '',
            width: booking.D_Width || '',
            height: booking.D_Height || '',
            pickupPostcode: booking.From_P_Code || '',
            deliveryPostcode: booking.To_P_Code || '',
            pickupCity: booking.From_City || '',
            deliveryCity: booking.To_City || '',
            pickupState: booking.From_State || '',
            deliveryState: booking.To_State || '',
            originCountry: booking.From_Country || 'AU',
            destinationCountry: booking.To_Country || 'AU',
            dangerousGoods: booking.DangerousGoods || (booking.isDangerous ? 'Other' : 'None'),
            specialHandling: booking.SpecialHandling || 'None',
            palletStackable: booking.Stackability === 'true' || booking.Stackability === true,
        });
        setSRDetails({
            senderFormData: {
                firstName: booking.Sender_Fname || '',
                lastName: booking.Sender_Lname || '',
                email: booking.Sender_Email || '',
                phone: booking.Sender_Phone || '',
                address1: booking.From_addr1 || '',
                address2: booking.From_addr2 || '',
                pickupInstructions: booking.From_PIns || '',
            },
            receiverFormData: {
                firstName: booking.Rec_Fname || '',
                lastName: booking.Rec_Lname || '',
                email: booking.Rec_Email || '',
                phone: booking.Rec_Phone || '',
                address1: booking.To_addr1 || '',
                address2: booking.To_addr2 || '',
                pickupInstructions: booking.To_DIns || '',
            },
        });
    };

    const deleteDraft = async (id = draftId) => {
        if (!id) return;
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${url}/api/drafts/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok && response.status !== 404) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.message || 'Unable to remove this draft.');
        }
        if (id === draftId) {
            setDraftId(null);
            setDraftUpdatedAt(null);
        }
    };

    // Context value
    const contextValue = {
        quoteData,
        quoteRequestBody,
        selectedQuote,
        SRDetails,
        formData,
        draftId,
        draftUpdatedAt,
        saveQuote,
        saveSRDetails,
        saveFormData,
        saveDraft,
        restoreDraft,
        deleteDraft,
        resetBookingFlow,
        applyBookingTemplate
    };

    return (
        <QuoteContext.Provider value={contextValue}>
            {children}
        </QuoteContext.Provider>
    );
}
